import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import PdfZoomPanWrap from './PdfZoomPanWrap';
import { useAuth } from '../contexts/AuthContext';
import BookFeedback from './BookFeedback';
import '../styles/SinghAmaraBook.css';
import '../styles/HanumanBahukBook.css';

import workerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
pdfjsLib.GlobalWorkerOptions.workerSrc = workerUrl;

interface Props {
  onNavigate?: (path: string) => void;
}

type PDFDocProxy = Awaited<ReturnType<typeof pdfjsLib.getDocument>['promise']>;
type BookEvent = 'book_open' | 'page_view' | 'book_close';

interface AnalyticsPayload {
  session_id: string;
  event: BookEvent;
  page?: number;
  total_pages?: number;
  duration_s?: number;
}

function fireAnalyticsSync(payload: AnalyticsPayload, token: string): void {
  fetch('/api/books/laukika-nyaya/analytics', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(payload),
    keepalive: true,
  }).catch(() => {});
}

export default function LaukikaNyayaBook({ onNavigate }: Props) {
  const { user, authLoading } = useAuth();
  const [fetching, setFetching]       = useState(false);
  const [error, setError]             = useState<string | null>(null);
  const [pdfDoc, setPdfDoc]           = useState<PDFDocProxy | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages]   = useState(0);
  const [rendering, setRendering]     = useState(false);
  const canvasRef     = useRef<HTMLCanvasElement>(null);
  const annotLayerRef = useRef<HTMLDivElement>(null);
  const renderTaskRef = useRef<{ cancel: () => void } | null>(null);
  const renderGenRef  = useRef<number>(0);
  const readerRef     = useRef<HTMLElement | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const sessionIdRef       = useRef<string | null>(null);
  const sessionOpenTimeRef = useRef<number | null>(null);
  const furthestPageRef    = useRef<number>(1);
  const totalPagesRef      = useRef<number>(0);
  const tokenRef           = useRef<string | null>(null);
  const sessionClosedRef   = useRef<boolean>(true);

  const [shareCopied, setShareCopied] = useState(false);

  const initialPage = useMemo(() => {
    const p = parseInt(new URLSearchParams(window.location.search).get('page') ?? '1', 10);
    return isNaN(p) || p < 1 ? 1 : p;
  }, []);
  const pendingPageRef = useRef<number>(initialPage);

  useEffect(() => { totalPagesRef.current = totalPages; }, [totalPages]);

  const flushSession = useCallback((): void => {
    if (sessionClosedRef.current) return;
    if (!sessionIdRef.current || !tokenRef.current) return;
    sessionClosedRef.current = true;
    const duration_s = sessionOpenTimeRef.current
      ? Math.round((Date.now() - sessionOpenTimeRef.current) / 1000)
      : 0;
    fireAnalyticsSync(
      { session_id: sessionIdRef.current, event: 'book_close',
        page: furthestPageRef.current, total_pages: totalPagesRef.current, duration_s },
      tokenRef.current,
    );
    sessionIdRef.current = null;
    tokenRef.current = null;
  }, []);

  useEffect(() => { return () => { flushSession(); }; }, [flushSession]);

  useEffect(() => {
    const onHidden = (): void => { if (document.visibilityState === 'hidden') flushSession(); };
    document.addEventListener('visibilitychange', onHidden);
    return () => document.removeEventListener('visibilitychange', onHidden);
  }, [flushSession]);

  const renderPage = useCallback(async (doc: PDFDocProxy, pageNum: number) => {
    const gen = ++renderGenRef.current;
    renderTaskRef.current?.cancel();
    renderTaskRef.current = null;

    const canvas = canvasRef.current;
    if (!canvas) return;
    setRendering(true);
    try {
      const page = await doc.getPage(pageNum);
      if (gen !== renderGenRef.current) return;

      const context = canvas.getContext('2d');
      if (!context) return;
      const dpr = window.devicePixelRatio || 1;
      const viewport0 = page.getViewport({ scale: 1 });
      const isFs = !!document.fullscreenElement;
      let containerWidth: number;
      if (isFs) {
        const availH    = window.innerHeight - 62;
        const widthByH  = (availH / viewport0.height) * viewport0.width;
        containerWidth  = Math.min(widthByH, window.innerWidth * 0.98);
      } else {
        // Use the pdf-canvas-wrap width so sizing is stable regardless of inner DOM depth
        const wrapEl    = canvas.closest('.pdf-canvas-wrap') as HTMLElement | null;
        containerWidth  = Math.min(wrapEl?.clientWidth ?? 800, 860);
      }
      const scale = (containerWidth / viewport0.width) * dpr;
      const viewport = page.getViewport({ scale });
      canvas.width  = viewport.width;
      canvas.height = viewport.height;
      canvas.style.width  = `${viewport.width  / dpr}px`;
      canvas.style.height = `${viewport.height / dpr}px`;
      const task = page.render({ canvas, canvasContext: context, viewport });
      renderTaskRef.current = task;
      await task.promise;
      renderTaskRef.current = null;

      // ── Annotation layer: make PDF-internal links (TOC) clickable ────────
      if (gen === renderGenRef.current) {
        const annLayer = annotLayerRef.current;
        if (annLayer) {
          annLayer.innerHTML = '';
          annLayer.style.width  = canvas.style.width;
          annLayer.style.height = canvas.style.height;
          try {
            const anns = await page.getAnnotations();
            for (const ann of anns) {
              if (ann.subtype !== 'Link' || !ann.rect) continue;
              const [x1, y1, x2, y2] = viewport.convertToViewportRectangle(ann.rect);
              const left   = `${Math.min(x1, x2) / dpr}px`;
              const top    = `${Math.min(y1, y2) / dpr}px`;
              const width  = `${Math.abs(x2 - x1) / dpr}px`;
              const height = `${Math.abs(y2 - y1) / dpr}px`;
              const el     = document.createElement('div');
              el.style.cssText = `left:${left};top:${top};width:${width};height:${height};`;
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const annAny = ann as any;
              const dest   = annAny.dest as string | unknown[] | null;
              const url    = annAny.url  as string | undefined;
              if (dest != null) {
                el.style.cursor = 'pointer';
                el.title = 'Go to page';
                el.addEventListener('click', async () => {
                  try {
                    let resolved: unknown = dest;
                    if (typeof resolved === 'string') resolved = await doc.getDestination(resolved);
                    if (Array.isArray(resolved) && resolved[0]) {
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      const idx = await doc.getPageIndex(resolved[0] as any);
                      setCurrentPage(Math.max(1, Math.min(idx + 1, totalPagesRef.current)));
                    }
                  } catch { /* ignore unresolvable link */ }
                });
              } else if (url) {
                el.style.cursor = 'alias';
                el.title = url;
                el.addEventListener('click', () => window.open(url, '_blank', 'noopener'));
              } else {
                continue;
              }
              annLayer.appendChild(el);
            }
          } catch { /* getAnnotations not available — skip silently */ }
        }
      }
    } catch (err: unknown) {
      if ((err as { name?: string })?.name !== 'RenderingCancelledException') {
        console.error('PDF render error', err);
      }
    } finally {
      if (gen === renderGenRef.current) setRendering(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (pdfDoc) renderPage(pdfDoc, currentPage);
  }, [pdfDoc, currentPage, renderPage]);

  useEffect(() => {
    if (!pdfDoc || sessionClosedRef.current || !sessionIdRef.current || !tokenRef.current) return;
    const sid   = sessionIdRef.current;
    const token = tokenRef.current;
    const page  = currentPage;
    if (page > furthestPageRef.current) furthestPageRef.current = page;
    const tid = setTimeout(() => {
      fireAnalyticsSync(
        { session_id: sid, event: 'page_view', page, total_pages: totalPagesRef.current },
        token,
      );
    }, 500);
    return () => clearTimeout(tid);
  }, [pdfDoc, currentPage]);

  useEffect(() => {
    if (user && !pdfDoc && !fetching && pendingPageRef.current > 1) {
      handleReadBook();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (pdfDoc && totalPages > 0 && pendingPageRef.current > 1) {
      const p = Math.max(1, Math.min(pendingPageRef.current, totalPages));
      pendingPageRef.current = 0;
      setCurrentPage(p);
    }
  }, [pdfDoc, totalPages]);

  useEffect(() => {
    if (!pdfDoc) return;
    const url = new URL(window.location.href);
    url.searchParams.set('page', String(currentPage));
    history.replaceState(null, '', url.toString());
  }, [pdfDoc, currentPage]);

  const handleReadBook = useCallback(async () => {
    if (!user) return;
    setError(null);
    setFetching(true);
    try {
      const token = await user.getIdToken();
      const resp = await fetch('/api/books/laukika-nyaya/laukika_nyaya_book.pdf', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (resp.status === 429) {
        const retryAfter = resp.headers.get('Retry-After');
        const minutes = retryAfter ? Math.ceil(Number(retryAfter) / 60) : null;
        throw new Error(
          minutes
            ? `Reading limit reached. Please try again in ${minutes} minute(s).`
            : 'Reading limit reached. Please try again later.',
        );
      }
      if (!resp.ok) throw new Error(`Server returned ${resp.status}`);

      const arrayBuffer = await resp.arrayBuffer();
      const doc   = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      const pages = doc.numPages;

      const newSessionId = crypto.randomUUID();
      sessionIdRef.current       = newSessionId;
      tokenRef.current           = token;
      sessionOpenTimeRef.current = Date.now();
      furthestPageRef.current    = 1;
      sessionClosedRef.current   = false;
      totalPagesRef.current      = pages;

      fireAnalyticsSync(
        { session_id: newSessionId, event: 'book_open', page: 1, total_pages: pages },
        token,
      );

      const targetPage = pendingPageRef.current > 1
        ? Math.max(1, Math.min(pendingPageRef.current, pages))
        : 1;
      pendingPageRef.current = 0;
      setTotalPages(pages);
      setCurrentPage(targetPage);
      setPdfDoc(doc);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load book. Please try again.');
    } finally {
      setFetching(false);
    }
  }, [user]);

  const goTo = useCallback((n: number) => {
    if (!pdfDoc) return;
    setCurrentPage(Math.max(1, Math.min(n, totalPages)));
  }, [pdfDoc, totalPages]);

  const handleSharePage = useCallback(async () => {
    const url = window.location.href;
    if (navigator.share) {
      await navigator.share({ title: document.title, url }).catch(() => {});
    } else {
      await navigator.clipboard.writeText(url);
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2200);
    }
  }, []);

  const closeReader = useCallback(() => {
    flushSession();
    setPdfDoc(null);
    setCurrentPage(1);
    setTotalPages(0);
  }, [flushSession]);

  const toggleFullscreen = useCallback(async () => {
    if (!readerRef.current) return;
    if (!document.fullscreenElement) {
      await readerRef.current.requestFullscreen().catch(() => {});
    } else {
      await document.exitFullscreen().catch(() => {});
    }
  }, []);

  useEffect(() => {
    if (!pdfDoc) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') goTo(currentPage + 1);
      else if (e.key === 'ArrowLeft') goTo(currentPage - 1);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [pdfDoc, currentPage, goTo]);

  useEffect(() => {
    const onFsChange = (): void => {
      const active = !!document.fullscreenElement;
      setIsFullscreen(active);
      if (pdfDoc) setTimeout(() => renderPage(pdfDoc, currentPage), 150);
    };
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, [pdfDoc, currentPage, renderPage]);

  if (authLoading) {
    return (
      <div className="singh-amara-page hanuman-bahuk-page">
        <div className="singh-amara-loading">
          <span className="loading-spinner" aria-hidden="true" />
          <p>Loading…</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="singh-amara-page hanuman-bahuk-page">
        <div className="singh-amara-gate">
          <div className="gate-lion hanuman-bahuk-gate-emblem" aria-hidden="true">📖</div>
          <h1 className="gate-title">लौकिक न्याय कोश</h1>
          <p className="gate-subtitle">Laukika Nyāya Kosha · ७४६ न्याय · संस्कृत · हिन्दी · अंग्रेज़ी</p>
          <p className="gate-body">
            यह पुस्तक पंजीकृत उपयोगकर्ताओं के लिए उपलब्ध है।
            <br />
            <span className="gate-body-en">This book is available to registered users.</span>
          </p>
          <button className="gate-btn hanuman-bahuk-gate-btn" onClick={() => onNavigate?.('/profile')}>
            साइन इन करें · Sign In
          </button>
          <button className="gate-back-btn" onClick={() => onNavigate?.('/products')}>
            ← उत्पाद / Products
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="singh-amara-page hanuman-bahuk-page">
      <nav className="singh-amara-nav">
        <button className="back-btn" onClick={() => onNavigate?.('/products')}>
          ← उत्पाद / Products
        </button>
      </nav>

      <header className="singh-amara-header hanuman-bahuk-header">
        <div className="header-emblem" aria-hidden="true">📖</div>
        <div className="header-text">
          <h1>लौकिक न्याय कोश — ७४६ भारतीय न्याय-वाक्य</h1>
          <p className="header-subtitle">Laukika Nyāya Kosha · 743 Indian Folk Maxims · Sanskrit · Hindi · English</p>
          <p className="header-meta">
            संस्कृत न्याय-वाक्य, शुद्ध हिन्दी अर्थ, अंग्रेज़ी व्याख्या और शास्त्र-प्रयोग सहित।
            स्रोत: ambuda.org · संपादक: रामसेवक · ३८१ पृष्ठ।
          </p>
        </div>
      </header>

      {error && <div className="singh-amara-error" role="alert">{error}</div>}

      {!pdfDoc && (
        <>
          <section className="singh-amara-actions">
            <button
              className="action-btn view-btn hanuman-bahuk-read-btn"
              onClick={handleReadBook}
              disabled={fetching}
              aria-busy={fetching}
            >
              {fetching
                ? <><span className="btn-spinner" aria-hidden="true" /> Loading Book…</>
                : '📖 पुस्तक पढ़ें · Read Book'}
            </button>
          </section>

          <section className="singh-amara-details">
            <div className="detail-card">
              <h2>पुस्तक में</h2>
              <ul>
                <li>📚 ७४६ लौकिक न्याय — वर्णक्रम-सूची सहित (क्लिक करने योग्य)</li>
                <li>🔤 संस्कृत न्याय-वाक्य, IAST लिप्यंतरण</li>
                <li>🇮🇳 शुद्ध हिन्दी अर्थ (तत्सम · अरबी/फ़ारसी-रहित)</li>
                <li>📐 अंग्रेज़ी व्याख्या एवं शास्त्र-प्रयोग (वेदान्त · न्याय · मीमांसा आदि)</li>
                <li>📋 सारणी-प्रारूप · US Letter लेटर · ३८१ पृष्ठ</li>
              </ul>
            </div>
            <div className="detail-card">
              <h2>Book Contents</h2>
              <ul>
                <li>📚 743 Laukika Nyayas — with clickable alphabetical index</li>
                <li>🔤 Sanskrit maxim + IAST transliteration</li>
                <li>🇮🇳 Pure Hindi meaning (tatsam vocabulary)</li>
                <li>📐 English explanation + Shastra usage (Vedanta · Nyaya · Mimamsa etc.)</li>
                <li>📋 Table layout · US Letter portrait · 381 pages</li>
              </ul>
            </div>
            <p className="read-only-notice">🔒 Available for online reading only.</p>
          </section>
        </>
      )}

      {pdfDoc && (
        <section className="pdf-reader" ref={readerRef as React.RefObject<HTMLElement>}>
          <div className="pdf-toolbar" onContextMenu={(e) => e.preventDefault()}>
            <button
              className="pdf-nav-btn"
              onClick={() => goTo(currentPage - 1)}
              disabled={currentPage <= 1 || rendering}
              aria-label="Previous page"
            >‹</button>

            <span className="pdf-page-info">
              {currentPage} / {totalPages}
            </span>

            <button
              className="pdf-nav-btn"
              onClick={() => goTo(currentPage + 1)}
              disabled={currentPage >= totalPages || rendering}
              aria-label="Next page"
            >›</button>

            <button
              className={`pdf-share-btn${shareCopied ? ' pdf-share-copied' : ''}`}
              onClick={handleSharePage}
              title={shareCopied ? 'Copied!' : 'Share page link'}
              aria-label={shareCopied ? 'Copied!' : 'Share page link'}
            >
              {shareCopied ? '✓' : '⎘'}
            </button>

            <button
              className="pdf-fullscreen-btn"
              onClick={toggleFullscreen}
              title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
              aria-label={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
            >
              {isFullscreen ? '⊠' : '⛶'}
            </button>

            <button
              className="pdf-close-btn"
              onClick={closeReader}
              aria-label="Close reader"
            >✕</button>
          </div>

          <PdfZoomPanWrap onContextMenu={(e) => e.preventDefault()} resetKey={currentPage}>
            <div className="pdf-canvas-container">
              {rendering && (
                <div className="pdf-rendering-overlay" aria-live="polite">
                  <span className="loading-spinner" aria-hidden="true" />
                </div>
              )}
              <canvas ref={canvasRef} className="pdf-canvas" />
              <div ref={annotLayerRef} className="pdf-annotation-layer" aria-hidden="true" />
            </div>
          </PdfZoomPanWrap>
        </section>
      )}
      {pdfDoc && (
        <BookFeedback
          bookId="laukika-nyaya"
          bookTitle="लौकिक न्याय कोश"
          page={currentPage}
        />
      )}
    </div>
  );
}
